// client/src/components/pages/LexiStar/PlanDispatch/RunBuilder.jsx
//
// Run builder (LS-09 §5.24, FR-TRIP-021): pick a truck, see its day as a run
// strip, and append the next trip with *Next load from here*. A truck with no
// run today gets "Start today's run" (POST /runs) first. The truck's run comes
// from its status-board row (`run.id`).

import React, { useCallback, useEffect, useState } from 'react';
import { FaStream, FaPlay } from 'react-icons/fa';
import ThemedSelect from '../../../common/ThemedSelect';
import {
  createRun,
  getRunSummary,
  listTrips,
  pickList,
} from '../../../../services/lexistarStatusBoard';
import { toApiDateString } from '../../../../utils/dateUtils';
import RunStrip from '../StatusBoard/RunStrip';
import { cardClasses, inputClasses } from '../StatusBoard/atoms';
import NextLoadPanel from './NextLoadPanel';
import UnplannedLoadPrompts from './UnplannedLoadPrompts';
import { normaliseRegistration } from './planLogic';

export default function RunBuilder({
  horses,
  sites,
  lanes,
  boardRows,
  prompts,
  siteName,
  onCreatePrompt,
  onDismissPrompt,
  promptBusyId,
  canCreate,
  onNotice,
}) {
  const [assetId, setAssetId] = useState('');
  const [runId, setRunId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const asset = horses.find((a) => a.id === assetId) || null;
  const assetKey = asset ? `reg:${normaliseRegistration(asset.registration)}` : null;

  useEffect(() => {
    if (!asset) {
      setRunId(null);
      setSummary(null);
      return;
    }
    const row = boardRows.find(
      (r) => (r.asset_id && r.asset_id === asset.id) || r.asset_key === assetKey
    );
    if (row?.run?.id) {
      setRunId(row.run.id);
      return;
    }
    // No run on the board row (no current trip): look for one among today's trips.
    let alive = true;
    setRunId(null);
    listTrips({ asset: asset.id, date: toApiDateString(new Date()) })
      .then((res) => {
        const withRun = pickList(res, 'trips').find((t) => t.run_id);
        if (alive) setRunId(withRun?.run_id || null);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, boardRows]);

  const load = useCallback(async () => {
    if (!runId) {
      setSummary(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setSummary(await getRunSummary(runId));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load the run');
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => {
    load();
  }, [load]);

  const startRun = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await createRun({
        asset_id: asset.id,
        asset_key: assetKey,
        shift_date: toApiDateString(new Date()),
      });
      setRunId(res.run?.id || res.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start the run');
      setLoading(false);
    }
  };

  const truckPrompts = assetKey ? prompts.filter((p) => p.asset_key === assetKey) : [];

  return (
    <section className={`${cardClasses} p-5`} aria-label="Run builder">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-sm font-semibold text-slate-900 inline-flex items-center gap-2">
            <FaStream className="text-slate-400" size={12} /> Runs — next load from here
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Chain the truck's next trip from where it is offloading; the driver gets it while still
            on the current trip.
          </p>
        </div>
        <div className="w-full sm:w-64">
          <ThemedSelect
            value={assetId}
            onChange={(e) => setAssetId(e.target.value)}
            className={inputClasses}
            aria-label="Truck"
          >
            <option value="">Choose a truck...</option>
            {horses.map((a) => (
              <option key={a.id} value={a.id}>
                {a.registration}
                {a.fleet_no ? ` (${a.fleet_no})` : ''}
              </option>
            ))}
          </ThemedSelect>
        </div>
      </div>

      {error && <p className="mb-3 text-sm text-red-700">{error}</p>}
      {truckPrompts.length > 0 && (
        <div className="mb-3">
          <UnplannedLoadPrompts
            events={truckPrompts}
            siteName={siteName}
            sites={sites}
            lanes={lanes}
            onCreate={onCreatePrompt}
            onDismiss={onDismissPrompt}
            busyId={promptBusyId}
            canCreate={canCreate}
          />
        </div>
      )}
      {!asset ? (
        <p className="text-sm text-slate-500">Choose a truck to see its run for today.</p>
      ) : loading && !summary ? (
        <p className="text-sm text-slate-500">Loading the run...</p>
      ) : summary ? (
        <RunStrip summary={summary}>
          {canCreate && (
            <NextLoadPanel
              key={summary.run?.id}
              summary={summary}
              onChanged={(msg) => {
                onNotice?.(msg);
                load();
              }}
            />
          )}
        </RunStrip>
      ) : (
        <div className="flex items-center justify-between gap-3 p-4 bg-slate-50 border border-slate-200 rounded-lg">
          <p className="text-sm text-slate-600">
            <span className="font-mono font-semibold">{asset.registration}</span> has no run today.
          </p>
          {canCreate && (
            <button
              type="button"
              onClick={startRun}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg"
            >
              <FaPlay size={10} /> Start today's run
            </button>
          )}
        </div>
      )}
    </section>
  );
}
